/**
 * FloorSelector
 *
 * Elevator-style floor selector for navigating floors within a building.
 * Used primarily in Headquarters for navigating between departments.
 */

import React from "react";
import { Building2, Users, DollarSign, Target } from "lucide-react";

export type FloorId = "lobby" | "hr" | "finance" | "strategy";

export interface Floor {
  id: FloorId;
  label: string;
  floorNumber: string;
  description: string;
  icon: React.FC<{ className?: string }>;
}

const HQ_FLOORS: Floor[] = [
  {
    id: "strategy",
    label: "Strategy",
    floorNumber: "4",
    description: "Planning & Budgets",
    icon: Target,
  },
  {
    id: "finance",
    label: "Finance",
    floorNumber: "3",
    description: "P&L Reports",
    icon: DollarSign,
  },
  {
    id: "hr",
    label: "HR",
    floorNumber: "2",
    description: "Hire & Manage Staff",
    icon: Users,
  },
  {
    id: "lobby",
    label: "Lobby",
    floorNumber: "G",
    description: "Reception",
    icon: Building2,
  },
];

export interface FloorSelectorProps {
  /** Currently selected floor */
  currentFloor: FloorId;
  /** Callback when a floor is selected */
  onFloorSelect: (floorId: FloorId) => void;
  /** Additional CSS classes */
  className?: string;
}

export const FloorSelector: React.FC<FloorSelectorProps> = ({
  currentFloor,
  onFloorSelect,
  className = "",
}) => {
  return (
    <div
      className={`flex flex-col bg-gray-900/95 backdrop-blur-md ${className}`}
      role="navigation"
      aria-label="Floor navigation"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10">
        <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
          Floors
        </h3>
      </div>

      {/* Floor Buttons */}
      <div className="flex-1 py-2">
        {HQ_FLOORS.map((floor) => {
          const Icon = floor.icon;
          const isCurrentFloor = currentFloor === floor.id;

          return (
            <button
              key={floor.id}
              onClick={() => onFloorSelect(floor.id)}
              className={`w-full flex items-center gap-3 px-3 py-3
                         transition-all duration-200 ${
                           isCurrentFloor
                             ? "bg-white/10 text-white border-l-4 border-theme-accent"
                             : "text-white/50 hover:text-white hover:bg-white/5 border-l-4 border-transparent"
                         }`}
              aria-current={isCurrentFloor ? "page" : undefined}
            >
              {/* Floor Number Indicator */}
              <div
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center
                           text-sm font-bold flex-shrink-0 transition-colors ${
                             isCurrentFloor
                               ? "bg-theme-accent text-white border-theme-accent"
                               : "bg-white/10 border-white/20 text-white/60"
                           }`}
              >
                {floor.floorNumber}
              </div>

              {/* Floor Info */}
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {floor.label}
                  </span>
                </div>
                <span className={`text-xs truncate block ${isCurrentFloor ? 'text-white/50' : 'text-white/30'}`}>
                  {floor.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Elevator base */}
      <div className="h-2 bg-white/5 border-t border-white/10" />
    </div>
  );
};

export default FloorSelector;
